/**
 * App state — in-memory cache synced to Redis via /api/store.
 * Call hydrateStore() once before rendering the app.
 */

const STORE_KEYS = [
  'companies',
  'scrapeSession',
  'emails',
  'seenEmails',
  'hiddenDomains',
  'hiddenEmails',
  'emailFilters',
  'scrapePrefs',
  'draftTemplate',
  'drafts',
  'agentContext',
  'draftCsvColumns',
  'contactAppendMode',
  'pulledDomains',
];

// Legacy browser localStorage keys (pre-Redis). Names kept so existing
// browser data still migrates; cleared after first successful hydrate.
const LEGACY_LS = {
  domains: 'homelead.domains',
  companies: 'homelead.companies',
  scrapeSession: 'homelead.scrapeSession',
  emails: 'homelead.emails',
  seenEmails: 'homelead.seenEmails',
  hiddenDomains: 'homelead.hiddenDomains',
  hiddenEmails: 'homelead.hiddenEmails',
  emailFilters: 'homelead.emailFilters',
  scrapePrefs: 'homelead.scrapePrefs',
  draftTemplate: 'homelead.draftTemplate',
  drafts: 'homelead.drafts',
  agentContext: 'homelead.agentContext',
  draftCsvColumns: 'homelead.draftCsvColumns',
  contactAppendMode: 'homelead.contactAppendMode',
  pulledDomains: 'homelead.pulledDomains',
};

const DEFAULT_HIDDEN = ['chromewebstore.google.com'];
const DEFAULT_HIDDEN_EMAILS = ['jane@doe.com'];

/** @type {Record<string, any>} */
const cache = {};
let hydrated = false;
/** @type {Set<string>} */
const dirty = new Set();
let flushTimer = null;
let flushPromise = null;

function normalizeEmail(email) {
  return String(email || '')
    .trim()
    .toLowerCase();
}

function readJson(key, fallback) {
  if (!(key in cache) || cache[key] === undefined) return fallback;
  return cache[key];
}

function writeJson(key, value) {
  cache[key] = value;
  dirty.add(key);
  scheduleFlush();
}

function scheduleFlush() {
  if (flushTimer) clearTimeout(flushTimer);
  flushTimer = setTimeout(() => {
    flushTimer = null;
    flushStore().catch(() => {});
  }, 80);
}

export async function flushStore() {
  if (!dirty.size) return;
  if (flushPromise) {
    await flushPromise;
    if (!dirty.size) return;
  }
  const payload = {};
  for (const key of dirty) {
    payload[key] = cache[key];
  }
  dirty.clear();
  flushPromise = fetch('/api/store', {
    method: 'PATCH',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify(payload),
  })
    .then(async (res) => {
      if (!res.ok) {
        const text = await res.text().catch(() => '');
        throw new Error(text || `Store flush failed (${res.status})`);
      }
    })
    .finally(() => {
      flushPromise = null;
    });
  await flushPromise;
}

function readLegacyLocal(key, fallback) {
  try {
    const raw = localStorage.getItem(key);
    if (!raw) return fallback;
    return JSON.parse(raw);
  } catch {
    return fallback;
  }
}

function collectLegacyLocalStorage() {
  const out = {};
  for (const [name, lsKey] of Object.entries(LEGACY_LS)) {
    if (name === 'domains') continue; // handled via companies migrate
    const val = readLegacyLocal(lsKey, undefined);
    if (val !== undefined && val !== null) out[name] = val;
  }
  // Migrate legacy domains[] → companies if needed
  if (!out.companies) {
    const legacy = readLegacyLocal(LEGACY_LS.domains, null);
    if (Array.isArray(legacy) && legacy.length) {
      out.companies = legacy;
    }
  }
  return out;
}

function clearLegacyLocalStorage() {
  try {
    for (const lsKey of Object.values(LEGACY_LS)) {
      localStorage.removeItem(lsKey);
    }
  } catch {
    /* ignore */
  }
}

/** Load Redis state into memory. Migrates leftover localStorage once. */
export async function hydrateStore() {
  const res = await fetch('/api/store');
  if (!res.ok) {
    throw new Error(`Failed to load store (${res.status})`);
  }
  const remote = await res.json();
  for (const key of STORE_KEYS) {
    if (key in remote) cache[key] = remote[key];
  }

  const hasRemote = STORE_KEYS.some((k) => k in remote);
  if (!hasRemote) {
    const legacy = collectLegacyLocalStorage();
    if (Object.keys(legacy).length) {
      for (const [k, v] of Object.entries(legacy)) {
        cache[k] = v;
        dirty.add(k);
      }
      await flushStore();
      clearLegacyLocalStorage();
    }
  } else {
    clearLegacyLocalStorage();
  }

  hydrated = true;
  return { ...cache };
}

export function isStoreHydrated() {
  return hydrated;
}

function normalizeDomain(domain) {
  return String(domain || '')
    .trim()
    .toLowerCase()
    .replace(/^www\./, '');
}

function isJunkDomain(domain) {
  const d = normalizeDomain(domain);
  return !d || d === 'producthunt.com' || d.endsWith('.producthunt.com');
}

function normalizeCompany(input) {
  if (!input) return null;
  if (typeof input === 'string') {
    const domain = normalizeDomain(input);
    if (isJunkDomain(domain)) return null;
    return {
      domain,
      product_name: '',
      tagline: '',
      description: '',
      votes_count: 0,
      ph_url: '',
      slug: '',
      website: '',
      launchedAt: '',
      savedAt: new Date().toISOString(),
    };
  }
  const domain = normalizeDomain(input.domain);
  if (isJunkDomain(domain)) return null;
  return {
    domain,
    product_name: input.product_name || input.name || '',
    tagline: input.tagline || '',
    description: input.description || '',
    votes_count: input.votes_count ?? input.votesCount ?? 0,
    ph_url: input.ph_url || '',
    slug: input.slug || '',
    website: input.website || input.original_url || '',
    launchedAt: input.launchedAt || input.featuredAt || input.createdAt || '',
    savedAt: input.savedAt || new Date().toISOString(),
  };
}

export function loadCompanies() {
  const list = readJson('companies', []);
  return Array.isArray(list) ? list.map((c) => normalizeCompany(c)).filter(Boolean) : [];
}

export function saveCompanies(companies) {
  const unique = [];
  const seen = new Set();
  for (const item of companies) {
    const company = normalizeCompany(item);
    if (!company || seen.has(company.domain)) continue;
    seen.add(company.domain);
    unique.push(company);
  }
  writeJson('companies', unique);
  return unique;
}

export function mergeCompanies(incoming, { prependNew = false } = {}) {
  const existing = loadCompanies();
  const byDomain = new Map(existing.map((c) => [c.domain, c]));
  const existingOrder = existing.map((c) => c.domain);
  const newDomains = [];

  for (const item of incoming) {
    const company = normalizeCompany(item);
    if (!company) continue;
    const prev = byDomain.get(company.domain);
    const wasNew = !prev;
    byDomain.set(company.domain, {
      ...(prev || {}),
      ...company,
      product_name: company.product_name || prev?.product_name || '',
      tagline: company.tagline || prev?.tagline || '',
      description: company.description || prev?.description || '',
      ph_url: company.ph_url || prev?.ph_url || '',
      slug: company.slug || prev?.slug || '',
      website: company.website || prev?.website || '',
      votes_count: company.votes_count || prev?.votes_count || 0,
      launchedAt: company.launchedAt || prev?.launchedAt || '',
      savedAt: prev?.savedAt || company.savedAt,
    });
    if (wasNew) newDomains.push(company.domain);
  }

  const order = prependNew
    ? [...newDomains, ...existingOrder.filter((d) => !newDomains.includes(d))]
    : [...existingOrder, ...newDomains.filter((d) => !existingOrder.includes(d))];

  return saveCompanies(order.map((d) => byDomain.get(d)).filter(Boolean));
}

export function loadScrapeSession() {
  const list = readJson('scrapeSession', []);
  if (!Array.isArray(list)) return [];
  return list.map((item) => normalizeCompany(item)).filter(Boolean);
}

export function saveScrapeSession(companies) {
  const unique = [];
  const seen = new Set();
  for (const item of companies) {
    const company = normalizeCompany(item);
    if (!company || seen.has(company.domain)) continue;
    seen.add(company.domain);
    unique.push(company);
  }
  writeJson('scrapeSession', unique);
  return unique;
}

export function clearScrapeSession() {
  writeJson('scrapeSession', []);
  return [];
}

export function upsertScrapeSessionCompany(item) {
  const company = normalizeCompany(item);
  if (!company) return loadScrapeSession();
  const rest = loadScrapeSession().filter((c) => c.domain !== company.domain);
  return saveScrapeSession([company, ...rest]);
}

export function loadDomains() {
  return loadCompanies().map((c) => c.domain);
}

export function saveDomains(domains) {
  return saveCompanies(domains).map((c) => c.domain);
}

export function mergeDomains(incoming) {
  return mergeCompanies(incoming).map((c) => c.domain);
}

export function getCompany(domain) {
  const clean = normalizeDomain(domain);
  return loadCompanies().find((c) => c.domain === clean) || null;
}

export function loadHiddenDomains() {
  const list = readJson('hiddenDomains', null);
  if (!Array.isArray(list)) {
    writeJson('hiddenDomains', DEFAULT_HIDDEN);
    return [...DEFAULT_HIDDEN];
  }
  const unique = [];
  const seen = new Set();
  for (const d of [...DEFAULT_HIDDEN, ...list]) {
    const clean = normalizeDomain(d);
    if (!clean || seen.has(clean)) continue;
    seen.add(clean);
    unique.push(clean);
  }
  return unique;
}

export function saveHiddenDomains(domains) {
  const unique = [];
  const seen = new Set();
  for (const d of domains) {
    const clean = normalizeDomain(d);
    if (!clean || seen.has(clean)) continue;
    seen.add(clean);
    unique.push(clean);
  }
  writeJson('hiddenDomains', unique);
  return unique;
}

export function hideDomain(domain) {
  const clean = normalizeDomain(domain);
  if (!clean) return loadHiddenDomains();
  return saveHiddenDomains([...loadHiddenDomains(), clean]);
}

export function unhideDomain(domain) {
  const clean = normalizeDomain(domain);
  return saveHiddenDomains(loadHiddenDomains().filter((d) => d !== clean));
}

export function isDomainHidden(domain, hiddenList = loadHiddenDomains()) {
  const clean = normalizeDomain(domain);
  const hidden = new Set(hiddenList.map(normalizeDomain));
  return hidden.has(clean);
}

export function loadHiddenEmails() {
  const list = readJson('hiddenEmails', null);
  if (!Array.isArray(list)) {
    writeJson('hiddenEmails', DEFAULT_HIDDEN_EMAILS);
    return [...DEFAULT_HIDDEN_EMAILS];
  }
  const unique = [];
  const seen = new Set();
  for (const e of [...DEFAULT_HIDDEN_EMAILS, ...list]) {
    const clean = normalizeEmail(e);
    if (!clean || !clean.includes('@') || seen.has(clean)) continue;
    seen.add(clean);
    unique.push(clean);
  }
  return unique;
}

export function saveHiddenEmails(emails) {
  const unique = [];
  const seen = new Set();
  for (const e of emails) {
    const clean = normalizeEmail(e);
    if (!clean || !clean.includes('@') || seen.has(clean)) continue;
    seen.add(clean);
    unique.push(clean);
  }
  writeJson('hiddenEmails', unique);
  return unique;
}

export function hideEmail(email) {
  const clean = normalizeEmail(email);
  if (!clean || !clean.includes('@')) return loadHiddenEmails();
  return saveHiddenEmails([...loadHiddenEmails(), clean]);
}

export function unhideEmail(email) {
  const clean = normalizeEmail(email);
  return saveHiddenEmails(loadHiddenEmails().filter((e) => e !== clean));
}

export function isEmailHidden(email, hiddenList = loadHiddenEmails()) {
  const clean = normalizeEmail(email);
  const hidden = new Set(hiddenList.map(normalizeEmail));
  return hidden.has(clean);
}

export function visibleDomains(allDomains = loadDomains(), hiddenList = loadHiddenDomains()) {
  const hidden = new Set(hiddenList.map(normalizeDomain));
  return allDomains.filter((d) => !hidden.has(normalizeDomain(d)));
}

export function visibleCompanies(
  allCompanies = loadCompanies(),
  hiddenList = loadHiddenDomains()
) {
  const hidden = new Set(hiddenList.map(normalizeDomain));
  return allCompanies.filter((c) => !hidden.has(normalizeDomain(c.domain)));
}

export function loadEmails() {
  const list = readJson('emails', []);
  return Array.isArray(list) ? list : [];
}

export function loadSeenEmails() {
  const list = readJson('seenEmails', []);
  return new Set(Array.isArray(list) ? list.map((e) => String(e).toLowerCase()) : []);
}

export function saveSeenEmails(setOrArray) {
  const list = Array.from(setOrArray).map((e) => String(e).toLowerCase());
  writeJson('seenEmails', list);
  return new Set(list);
}

export function mergeEmails(incoming) {
  const existing = loadEmails();
  const seen = loadSeenEmails();
  const added = [];
  let skipped = 0;

  for (const row of incoming) {
    const email = String(row.email || '').trim().toLowerCase();
    if (!email) continue;
    if (seen.has(email)) {
      skipped += 1;
      continue;
    }
    seen.add(email);
    const company = getCompany(row.domain) || {};
    const record = {
      email,
      domain: row.domain || company.domain || '',
      classification: row.classification || '',
      verification: row.verification || '',
      score: row.score ?? null,
      product_name: row.product_name || company.product_name || '',
      tagline: row.tagline || company.tagline || '',
      description: row.description || company.description || '',
      votes_count: row.votes_count ?? company.votes_count ?? 0,
      ph_url: row.ph_url || company.ph_url || '',
      slug: row.slug || company.slug || '',
      website: row.website || company.website || '',
      launchedAt: row.launchedAt || company.launchedAt || '',
      savedAt: new Date().toISOString(),
    };
    existing.push(record);
    added.push(record);
  }

  writeJson('emails', existing);
  saveSeenEmails(seen);
  return { added, skipped, emails: existing };
}

export function clearEmails() {
  writeJson('emails', []);
}

export function clearSeenEmails() {
  writeJson('seenEmails', []);
}

export function loadPulledDomains() {
  const list = readJson('pulledDomains', []);
  const set = new Set(
    (Array.isArray(list) ? list : []).map(normalizeDomain).filter(Boolean)
  );
  let grew = false;
  loadEmails().forEach((row) => {
    const key = normalizeDomain(row?.domain);
    if (key && !set.has(key)) {
      set.add(key);
      grew = true;
    }
  });
  if (grew) writeJson('pulledDomains', Array.from(set));
  return set;
}

export function savePulledDomains(setOrArray) {
  const list = Array.from(setOrArray).map(normalizeDomain).filter(Boolean);
  writeJson('pulledDomains', list);
  return new Set(list);
}

export function markDomainPulled(domain) {
  const key = normalizeDomain(domain);
  if (!key) return loadPulledDomains();
  const set = loadPulledDomains();
  set.add(key);
  return savePulledDomains(set);
}

export function clearPulledDomains() {
  writeJson('pulledDomains', []);
}

export function loadEmailFilters(defaults) {
  const saved = readJson('emailFilters', null);
  if (!saved || typeof saved !== 'object') return defaults;
  return {
    verifications: Array.isArray(saved.verifications)
      ? saved.verifications
      : defaults.verifications,
    classifications: Array.isArray(saved.classifications)
      ? saved.classifications
      : defaults.classifications,
  };
}

export function saveEmailFilters(filters) {
  writeJson('emailFilters', filters);
}

export function loadScrapePrefs(defaults) {
  const saved = readJson('scrapePrefs', null);
  if (!saved || typeof saved !== 'object') return defaults;
  return { ...defaults, ...saved };
}

export function saveScrapePrefs(prefs) {
  writeJson('scrapePrefs', prefs);
}

export const DEFAULT_PULL = {
  project: 'a Project on their to-do list',
  unavoidable: 'Unavoidable right now',
  looking: 'the option(s) they Look into',
  lacking: 'Lacking for some critical reason(s)',
};

export function composePullHypothesis(pull = DEFAULT_PULL) {
  const p = { ...DEFAULT_PULL, ...(pull || {}) };
  return `There is ${p.project} that is ${p.unavoidable}, and ${p.looking} are ${p.lacking}.`;
}

export const DEFAULT_DRAFT_TEMPLATE = {
  subject: 'Loved {{product_name}} on Product Hunt',
  body: `Hey {{first_name}},

Saw {{product_name}} ({{domain}}) on Product Hunt — "{{tagline}}".

Would love to share a quick idea that might help.

— Nic`,
  pull: { ...DEFAULT_PULL },
};

export function loadDraftTemplate() {
  const saved = readJson('draftTemplate', null);
  if (!saved || typeof saved !== 'object') {
    return {
      ...DEFAULT_DRAFT_TEMPLATE,
      pull: { ...DEFAULT_PULL },
    };
  }
  return {
    subject: saved.subject || DEFAULT_DRAFT_TEMPLATE.subject,
    body: saved.body || DEFAULT_DRAFT_TEMPLATE.body,
    pull: {
      ...DEFAULT_PULL,
      ...(saved.pull && typeof saved.pull === 'object' ? saved.pull : {}),
    },
  };
}

export function saveDraftTemplate(template) {
  writeJson('draftTemplate', template);
}

export function loadDrafts() {
  const list = readJson('drafts', []);
  return Array.isArray(list) ? list : [];
}

export function saveDrafts(drafts) {
  writeJson('drafts', Array.isArray(drafts) ? drafts : []);
  return drafts;
}

export function clearDrafts() {
  writeJson('drafts', []);
}

export function draftIdForContact(row) {
  return `${row.email}::${row.domain || ''}`;
}

export function buildDraftFromContact(row, template = loadDraftTemplate()) {
  const company = getCompany(row.domain) || {};
  const enriched = { ...company, ...row };
  const pullSentence = composePullHypothesis(template?.pull);
  const vars = {
    ...buildTemplateVars(enriched),
    pull_hypothesis: pullSentence,
  };
  const subject = applyTemplate(template.subject, vars);
  const body = applyTemplate(template.body, vars);
  return {
    id: draftIdForContact(row),
    ...vars,
    subject,
    finished_email: body,
    body,
    updatedAt: new Date().toISOString(),
  };
}

/** One draft per contact; uses stored overrides when present. */
export function draftsFromContacts(
  contacts,
  draftById = {},
  template = null
) {
  const tpl = template || loadDraftTemplate();
  const overrides =
    draftById instanceof Map
      ? draftById
      : new Map(Object.entries(draftById || {}));

  return (contacts || []).map((row) => {
    const id = draftIdForContact(row);
    return overrides.get(id) || buildDraftFromContact(row, tpl);
  });
}

/** Keep drafts 1:1 with visible contacts. Preserves existing draft text by default. */
export function syncDraftsFromContacts(
  contacts,
  existingDrafts = [],
  template = null,
  { preserveExisting = true } = {}
) {
  const tpl = template || loadDraftTemplate();
  const byId = preserveExisting
    ? new Map((existingDrafts || []).map((d) => [d.id, d]))
    : new Map();

  const next = (contacts || []).map((row) => {
    const id = draftIdForContact(row);
    if (preserveExisting && byId.has(id)) return byId.get(id);
    return buildDraftFromContact(row, tpl);
  });

  return saveDrafts(next);
}

export const DEFAULT_AGENT_CONTEXT = [
  {
    id: 'rules',
    title: 'Rules',
    body: 'list tools as code nodes in markdown',
  },
];

export function loadAgentContext() {
  const saved = readJson('agentContext', null);
  if (!Array.isArray(saved) || saved.length === 0) {
    writeJson('agentContext', DEFAULT_AGENT_CONTEXT);
    return DEFAULT_AGENT_CONTEXT.map((b) => ({ ...b }));
  }
  return saved
    .map((block, i) => ({
      id: String(block?.id || `block-${i}`),
      title: String(block?.title || 'Context'),
      body: String(block?.body || ''),
    }))
    .filter((b) => b.id);
}

export function saveAgentContext(blocks) {
  const next = (Array.isArray(blocks) ? blocks : [])
    .map((block, i) => ({
      id: String(block?.id || `block-${i}`),
      title: String(block?.title || 'Context'),
      body: String(block?.body || ''),
    }))
    .filter((b) => b.id);
  if (!next.some((b) => b.id === 'rules')) {
    next.unshift({ ...DEFAULT_AGENT_CONTEXT[0] });
  }
  writeJson('agentContext', next);
  return next;
}

export function loadDraftCsvColumns(allColumns) {
  const saved = readJson('draftCsvColumns', null);
  if (!Array.isArray(saved) || !saved.length) return [...allColumns];
  const allowed = new Set(allColumns);
  const selected = saved.filter((c) => allowed.has(c));
  return selected.length ? selected : [...allColumns];
}

export function saveDraftCsvColumns(columns) {
  const list = Array.isArray(columns) ? columns : [];
  writeJson('draftCsvColumns', list);
  return list;
}

export function loadContactAppendMode() {
  const saved = readJson('contactAppendMode', null);
  if (typeof saved === 'boolean') return saved;
  return true;
}

export function saveContactAppendMode(value) {
  const next = Boolean(value);
  writeJson('contactAppendMode', next);
  return next;
}

export function firstNameFromEmail(email) {
  const local = String(email || '').split('@')[0] || '';
  const token = local.split(/[._+-]/)[0] || local;
  if (!token) return 'there';
  return token.charAt(0).toUpperCase() + token.slice(1).toLowerCase();
}

export function applyTemplate(template, vars) {
  return String(template || '').replace(/\{\{\s*(\w+)\s*\}\}/g, (_, key) => {
    const value = vars[key];
    return value == null ? '' : String(value);
  });
}

export function buildTemplateVars(row) {
  const first_name = firstNameFromEmail(row.email);
  return {
    email: row.email || '',
    first_name,
    domain: row.domain || '',
    product_name: row.product_name || '',
    tagline: row.tagline || '',
    description: row.description || '',
    votes_count: row.votes_count ?? '',
    ph_url: row.ph_url || '',
    slug: row.slug || '',
    website: row.website || '',
    classification: row.classification || '',
    verification: row.verification || '',
    score: row.score ?? '',
  };
}

export function downloadCsv(filename, header, rows) {
  const lines = [header, ...rows].map((row) =>
    row
      .map((cell) => {
        const value = cell == null ? '' : String(cell);
        if (/[",\n]/.test(value)) return `"${value.replace(/"/g, '""')}"`;
        return value;
      })
      .join(',')
  );
  const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const link = document.createElement('a');
  link.href = url;
  link.download = filename;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}
